import {
  useGroupTagGroups,
  useGroupTagMutations,
  useGroupTypeMutations,
  useGroupTypes,
  useAccess,
  type GroupTagGroup,
  type GroupType,
} from '@church/data'
import type { GROUP_ENROLLMENTS, GROUP_VISIBILITIES } from '@church/domain'
import {
  faEyeSlash,
  faFloppyDisk,
  faGlobe,
  faLock,
  faPeopleGroup,
  faPlus,
  faTags,
  faTrashCan,
  faXmark,
} from '@church/ui-icons'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import {
  ListCard,
  ListRow,
  ListSection,
  NewButton,
  usePanel,
} from '../components/platform/SettingsList.tsx'
import {
  Button,
  Card,
  Chip,
  ConfirmButton,
  PanelPrimary,
  EmptyState,
  ErrorNote,
  Field,
  Icon,
  IconButton,
  Input,
  PanelSection,
  SidePanel,
  Spinner,
  Textarea,
} from '../components/ui.tsx'

/**
 * Group types — the settings screen where a church decides, once per category,
 * who can find these groups and how people get in.
 *
 * These three switches are the whole privacy model for groups, so each one is
 * spelled out in plain words next to it. "Private" here is what keeps a
 * recovery group out of the directory, and it is not a setting to bury.
 */
export const Route = createFileRoute('/_authed/_shell/settings/group-types')({
  component: GroupTypesScreen,
})

const VISIBILITY_HELP: Record<(typeof GROUP_VISIBILITIES)[number], string> = {
  public: 'Anyone signed in can find these groups in the directory.',
  members: 'Only people already in the group can see it.',
  private: 'Staff and the group’s own leaders only — nobody else knows it exists.',
}

const ENROLLMENT_HELP: Record<(typeof GROUP_ENROLLMENTS)[number], string> = {
  open: 'Anyone can join straight away.',
  request: 'People ask, and a leader approves.',
  invite: 'Only a leader or staff can add someone.',
}

const VISIBILITY_LABEL: Record<(typeof GROUP_VISIBILITIES)[number], string> = {
  public: 'Anyone in the church',
  members: 'Only their members',
  private: 'Staff and leaders only',
}

const ENROLLMENT_LABEL: Record<(typeof GROUP_ENROLLMENTS)[number], string> = {
  open: 'Join straight away',
  request: 'Ask a leader',
  invite: 'Invitation only',
}

const VISIBILITY_CHIP: Record<(typeof GROUP_VISIBILITIES)[number], string> = {
  public: 'Public',
  members: 'Members see it',
  private: 'Private',
}

const ENROLLMENT_CHIP: Record<(typeof GROUP_ENROLLMENTS)[number], string> = {
  open: 'Open',
  request: 'By request',
  invite: 'Invite only',
}

function GroupTypesScreen() {
  const { data: types, isPending } = useGroupTypes()
  const { remove } = useGroupTypeMutations()
  const access = useAccess()
  const panel = usePanel<string>()
  const type = panel.shown === 'new' ? undefined : types?.find((t) => t.id === panel.shown)

  return (
    <div className="max-w-2xl">
      <ListSection
        title="Group types"
        icon={faPeopleGroup}
        hint="Every group has a type, and the type decides who can see it and how people join."
        action={
          access.has('groups', 'administrator') && (
            <NewButton label="New type" onClick={() => panel.show('new')} />
          )
        }
      >
        {isPending ? (
          <Spinner />
        ) : !types || types.length === 0 ? (
          <Card>
            <EmptyState
              title="No group types yet"
              hint="Start with “Small Group” and “Ministry Team” — you can add more whenever."
            />
          </Card>
        ) : (
          <ListCard>
            {types.map((t) => (
              <ListRow
                key={t.id}
                title={t.name}
                meta={`${t.groupCount} ${t.groupCount === 1 ? 'group' : 'groups'}${
                  t.description ? ` · ${t.description}` : ''
                }`}
                aside={
                  <>
                    <Chip tone={t.visibility === 'private' ? 'warning' : 'neutral'}>
                      <Icon
                        icon={t.visibility === 'private' ? faLock : faGlobe}
                        className="size-3"
                      />
                      {VISIBILITY_CHIP[t.visibility]}
                    </Chip>
                    <span className="hidden sm:contents">
                      <Chip>{ENROLLMENT_CHIP[t.enrollment]}</Chip>
                    </span>
                  </>
                }
                onOpen={access.has('groups', 'administrator') ? () => panel.show(t.id) : undefined}
              />
            ))}
          </ListCard>
        )}
      </ListSection>

      <SidePanel
        open={panel.open && (panel.shown === 'new' || type !== undefined)}
        onOpenChange={panel.onOpenChange}
        title={type ? type.name : 'New group type'}
        description={
          type ? `${type.groupCount} ${type.groupCount === 1 ? 'group' : 'groups'}` : undefined
        }
        footer={
          type ? (
            <ConfirmButton
              label="Delete type"
              icon={faTrashCan}
              variant="quietDanger"
              className="-ml-4"
              disabled={type.groupCount > 0 || remove.isPending}
              title={`Delete “${type.name}”?`}
              description="Only an unused type can be deleted — move its groups to another type first."
              confirmLabel="Delete type"
              onConfirm={() => remove.mutate(type.id, { onSuccess: panel.close })}
            />
          ) : undefined
        }
      >
        <TypeForm key={type?.id ?? 'new'} existing={type} onDone={panel.close} />
        {type && type.groupCount > 0 && (
          <p className="text-xs text-content-muted">
            To delete this type, move its groups to another type first.
          </p>
        )}
        <ErrorNote error={remove.error} />
      </SidePanel>

      <div className="mt-8">
        <GroupTagsSection />
      </div>
    </div>
  )
}

/**
 * Group tags, by category: what people pick a group by in the directory —
 * Day, Life stage, Area. A category is offered as a filter unless it is
 * kept for staff.
 */
function GroupTagsSection() {
  const { data: facets, isPending } = useGroupTagGroups()
  const { removeFacet } = useGroupTagMutations()
  const access = useAccess()
  const panel = usePanel<string>()
  const facet = panel.shown === 'new' ? undefined : facets?.find((f) => f.id === panel.shown)
  const admin = access.has('groups', 'administrator')

  return (
    <>
      <ListSection
        title="Group tags"
        icon={faTags}
        hint="Categories people find a group by in the directory, each with its tags."
        action={admin && <NewButton label="New tag category" onClick={() => panel.show('new')} />}
      >
        {isPending ? (
          <Spinner />
        ) : !facets || facets.length === 0 ? (
          <Card>
            <EmptyState
              title="No tag categories yet"
              hint="Try “Day” (Tuesday, Thursday…) and “Life stage” (Young adults, Families…)."
            />
          </Card>
        ) : (
          <ListCard>
            {facets.map((f) => (
              <ListRow
                key={f.id}
                title={f.name}
                meta={
                  f.tags.length === 0 ? 'No tags yet' : f.tags.map((tag) => tag.name).join(', ')
                }
                aside={
                  <>
                    {!f.shownInDirectory && (
                      <Chip tone="warning">
                        <Icon icon={faEyeSlash} className="size-3" />
                        Staff only
                      </Chip>
                    )}
                    <span className="hidden sm:contents">
                      <Chip>{f.multiple ? 'Several' : 'One each'}</Chip>
                    </span>
                  </>
                }
                onOpen={admin ? () => panel.show(f.id) : undefined}
              />
            ))}
          </ListCard>
        )}
      </ListSection>

      <SidePanel
        open={panel.open && (panel.shown === 'new' || facet !== undefined)}
        onOpenChange={panel.onOpenChange}
        title={facet ? facet.name : 'New tag category'}
        footer={
          facet ? (
            <ConfirmButton
              label="Delete category"
              icon={faTrashCan}
              variant="quietDanger"
              className="-ml-4"
              disabled={removeFacet.isPending}
              title={`Delete “${facet.name}”?`}
              description="Its tags come off every group that has them."
              confirmLabel="Delete category"
              onConfirm={() => removeFacet.mutate(facet.id, { onSuccess: panel.close })}
            />
          ) : undefined
        }
      >
        <FacetForm key={facet?.id ?? 'new'} existing={facet} onCreated={(id) => panel.show(id)} />
        {facet && <FacetTags facet={facet} />}
        <ErrorNote error={removeFacet.error} />
      </SidePanel>
    </>
  )
}

function FacetForm({
  existing,
  onCreated,
}: {
  existing?: GroupTagGroup
  onCreated: (id: string) => void
}) {
  const { createFacet, updateFacet } = useGroupTagMutations()
  const [form, setForm] = useState({
    name: existing?.name ?? '',
    multiple: existing?.multiple ?? false,
    shownInDirectory: existing?.shownInDirectory ?? true,
  })
  const mutation = existing ? updateFacet : createFacet

  return (
    <div className="space-y-4">
      <Field label="Name">
        <Input
          placeholder="Day"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        />
      </Field>
      <PanelSection title="Choices">
        <label className="flex items-center gap-2 text-sm text-content-default">
          <input
            type="checkbox"
            className="accent-brand-solid"
            checked={form.multiple}
            onChange={(e) => setForm((f) => ({ ...f, multiple: e.target.checked }))}
          />
          A group can have more than one of these tags
        </label>
        <label className="flex items-center gap-2 text-sm text-content-default">
          <input
            type="checkbox"
            className="accent-brand-solid"
            checked={form.shownInDirectory}
            onChange={(e) => setForm((f) => ({ ...f, shownInDirectory: e.target.checked }))}
          />
          Offer it as a filter in the directory
        </label>
      </PanelSection>
      <ErrorNote error={mutation.error} />
      <PanelPrimary>
        <Button
          icon={existing ? faFloppyDisk : faPlus}
          className="py-1.5"
          disabled={form.name.trim() === ''}
          pending={mutation.isPending}
          onClick={() => {
            const body = { ...form, name: form.name.trim() }
            if (existing) updateFacet.mutate({ id: existing.id, ...body })
            else createFacet.mutate(body, { onSuccess: (created) => onCreated(created.id) })
          }}
        >
          {existing ? 'Save' : 'Create'}
        </Button>
      </PanelPrimary>
    </div>
  )
}

/** A category's tags: add one, rename one, take one away. */
function FacetTags({ facet }: { facet: GroupTagGroup }) {
  const { createTag, updateTag, removeTag } = useGroupTagMutations()
  const [name, setName] = useState('')
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null)

  return (
    <PanelSection title="Tags">
      {facet.tags.length > 0 && (
        <ul className="divide-y divide-border-subtle rounded-lg border border-border-subtle">
          {facet.tags.map((tag) => (
            <li key={tag.id} className="flex items-center gap-2 px-3 py-2">
              {editing?.id === tag.id ? (
                <Input
                  aria-label={`Rename ${tag.name}`}
                  value={editing.name}
                  autoFocus
                  onChange={(e) => setEditing({ id: tag.id, name: e.target.value })}
                  onBlur={() => {
                    const next = editing.name.trim()
                    if (next !== '' && next !== tag.name)
                      updateTag.mutate({ id: tag.id, name: next })
                    setEditing(null)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur()
                    if (e.key === 'Escape') setEditing(null)
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="min-w-0 flex-1 truncate text-left text-sm text-content-default"
                  onClick={() => setEditing({ id: tag.id, name: tag.name })}
                >
                  {tag.name}
                </button>
              )}
              <span className="text-xs text-content-muted tabular-nums">
                {tag.groupCount} {tag.groupCount === 1 ? 'group' : 'groups'}
              </span>
              <IconButton
                icon={faXmark}
                label={`Remove ${tag.name}`}
                variant="quietDanger"
                disabled={removeTag.isPending}
                onClick={() => removeTag.mutate(tag.id)}
              />
            </li>
          ))}
        </ul>
      )}
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim() === '') return
          createTag.mutate(
            { tagGroupId: facet.id, name: name.trim() },
            { onSuccess: () => setName('') },
          )
        }}
      >
        <Input
          aria-label="New tag"
          placeholder="New tag"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <IconButton
          icon={faPlus}
          label="Add tag"
          variant="secondary"
          type="submit"
          disabled={createTag.isPending || name.trim() === ''}
        />
      </form>
      <ErrorNote error={createTag.error ?? updateTag.error ?? removeTag.error} />
    </PanelSection>
  )
}

/** A choice of one, each with the plain-words consequence under it. */
function Choices<T extends string>({
  name,
  value,
  options,
  labels,
  help,
  onChange,
}: {
  name: string
  value: T
  options: readonly T[]
  labels: Record<T, string>
  help: Record<T, string>
  onChange: (value: T) => void
}) {
  return (
    <div className="space-y-1.5">
      {options.map((option) => (
        <label
          key={option}
          className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 ${
            value === option
              ? 'border-brand-solid bg-brand-soft'
              : 'border-border-default hover:bg-surface-sunken'
          }`}
        >
          <input
            type="radio"
            name={name}
            className="mt-1 accent-brand-solid"
            checked={value === option}
            onChange={() => onChange(option)}
          />
          <span>
            <span className="block text-sm font-medium text-content-default">{labels[option]}</span>
            <span className="block text-xs text-content-muted">{help[option]}</span>
          </span>
        </label>
      ))}
    </div>
  )
}

function TypeForm({ existing, onDone }: { existing?: GroupType; onDone: () => void }) {
  const { create, update } = useGroupTypeMutations()
  const [form, setForm] = useState({
    name: existing?.name ?? '',
    description: existing?.description ?? '',
    visibility: existing?.visibility ?? 'public',
    enrollment: existing?.enrollment ?? 'request',
    rosterVisibleToMembers: existing?.rosterVisibleToMembers ?? true,
  })

  const mutation = existing ? update : create
  const submit = () => {
    const body = {
      name: form.name.trim(),
      description: form.description.trim() === '' ? null : form.description.trim(),
      visibility: form.visibility,
      enrollment: form.enrollment,
      rosterVisibleToMembers: form.rosterVisibleToMembers,
    }
    if (existing) update.mutate({ id: existing.id, ...body }, { onSuccess: onDone })
    else create.mutate(body, { onSuccess: onDone })
  }

  return (
    <div className="space-y-6">
      <Field label="Name">
        <Input
          placeholder="Small Group"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        />
      </Field>
      <Field label="Description">
        <Textarea
          rows={2}
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        />
      </Field>

      <PanelSection title="Who can find these groups">
        <Choices
          name="visibility"
          value={form.visibility}
          options={['public', 'members', 'private'] as const}
          labels={VISIBILITY_LABEL}
          help={VISIBILITY_HELP}
          onChange={(visibility) => setForm((f) => ({ ...f, visibility }))}
        />
      </PanelSection>

      <PanelSection title="How people join">
        <Choices
          name="enrollment"
          value={form.enrollment}
          options={['open', 'request', 'invite'] as const}
          labels={ENROLLMENT_LABEL}
          help={ENROLLMENT_HELP}
          onChange={(enrollment) => setForm((f) => ({ ...f, enrollment }))}
        />
      </PanelSection>

      <PanelSection title="Roster">
        <label className="flex items-center gap-2 text-sm text-content-default">
          <input
            type="checkbox"
            className="accent-brand-solid"
            checked={form.rosterVisibleToMembers}
            onChange={(e) => setForm((f) => ({ ...f, rosterVisibleToMembers: e.target.checked }))}
          />
          Members can see who else is in the group
        </label>
      </PanelSection>

      <ErrorNote error={mutation.error} />
      <PanelPrimary>
        <Button
          icon={existing ? faFloppyDisk : faPlus}
          className="py-1.5"
          disabled={form.name.trim() === ''}
          pending={mutation.isPending}
          onClick={submit}
        >
          {existing ? 'Save' : 'Create'}
        </Button>
      </PanelPrimary>
    </div>
  )
}
